import { useState } from "react";
import { Badge, Button, Card, Grid, Group, RingProgress, SimpleGrid, Stack, Table, Text, Textarea, Title } from "@mantine/core";
import { IconCalendarStats } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { BulletSection, KitchenPending, NutritionChips, PreferencesFields } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { buildPreferences, kitchenRequest, parseDayLog, type PreferencesForm } from "../lib/kitchen";

const SAMPLE_DAY = JSON.stringify(
  [
    { meal: "breakfast", time: "07:30", foods: [{ name: "plain bagel", amount: "1 large" }, { name: "cream cheese", amount: "2 tbsp" }, { name: "latte, whole milk", amount: "16 oz" }] },
    { meal: "lunch", time: "12:45", foods: [{ name: "chicken caesar wrap", amount: "1" }, { name: "potato chips", amount: "1 small bag" }] },
    { meal: "snack", time: "15:30", foods: [{ name: "granola bar", amount: "1" }] },
  ],
  null,
  2,
);

const STATUS_COLORS = { low: "orange", on_track: "lime.8", high: "red" } as const;
const scoreColor = (s: number) => (s >= 75 ? "lime" : s >= 50 ? "orange" : "red");

export function DailyReviewPage() {
  const [log, setLog] = useState("");
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [prefs, setPrefs] = useState<PreferencesForm>({ targets: { calories: 2000, proteinGrams: 120, fiberGrams: 30, sodiumMg: 2300 }, timeOfDay: "afternoon" });
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.tools.dailyReview(req) });

  const ready = Boolean(log.trim() || input.text.trim() || input.media);
  const submit = () => {
    if (!ready) return;
    const req = kitchenRequest({ text: input.text, media: input.media, dayLog: parseDayLog(log), message: composeMessage(note), preferences: buildPreferences(prefs) });
    setLast(req);
    m.mutate(req);
  };
  const data = m.data?.data;

  return (
    <RequireFeature feature="health">
      <PageHeader
        eyebrow="POST /napi/tools/dailyReview"
        title="Daily nutrition review"
        description="Send the day's food log in whatever shape your app stores it, plus targets. You get a score, what's low or high, what's left, and three next meals that fit. Totals are AI estimates from the log."
        actions={<RouteModelPicker routes={["dailyReview"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <Textarea
                label="Today's log"
                description="JSON in any shape, or plain text. Sent as dayLog."
                autosize
                minRows={5}
                maxRows={14}
                ff="monospace"
                fz="xs"
                placeholder='[{"meal": "breakfast", "foods": [...]}]'
                value={log}
                onChange={(e) => setLog(e.currentTarget.value)}
              />
              {!log && (
                <Button variant="subtle" color="violet" size="xs" onClick={() => setLog(SAMPLE_DAY)}>
                  Use a sample day
                </Button>
              )}
              <Text fz="xs" c="dimmed" mb={-8}>
                Or add a screenshot of a food diary, or a voice note about the day:
              </Text>
              <MediaInput value={input} onChange={setInput} kinds={["image", "audio"]} textPlaceholder="Anything else? e.g. skipped dinner, feeling low on energy" photoLabel="Drop a screenshot of the day's food log" />
              <PreferencesFields value={prefs} onChange={setPrefs} fields={["targets", "goals", "diet", "allergies", "conditions", "timeOfDay"]} defaultOpen />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. training day, long run this morning" />
              <Button leftSection={<IconCalendarStats size={16} />} onClick={submit} loading={m.isPending} disabled={!ready} size="md">
                Review my day
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && m.mutate(last)} />}
          {m.isPending && <KitchenPending title="Reviewing the day…" />}
          {data && !m.isPending && (
            <Stack gap="md">
              <ResultHeader title="Your day" meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              <Card>
                <Group wrap="nowrap" align="center" gap="lg">
                  <RingProgress
                    size={96}
                    thickness={9}
                    roundCaps
                    sections={[{ value: data.score, color: scoreColor(data.score) }]}
                    label={
                      <Text ta="center" fw={750} fz="lg">
                        {data.score}
                      </Text>
                    }
                  />
                  <Stack gap={6} style={{ flex: 1 }}>
                    {data.summary && <Text>{data.summary}</Text>}
                    <NutritionChips n={data.estimatedTotals} size="xs" label="so far (AI estimate)" />
                    <NutritionChips n={data.remainingTargets} size="xs" label="left to target" />
                  </Stack>
                </Group>
              </Card>
              {data.flags.length > 0 && (
                <Card>
                  <Table verticalSpacing={6} fz="sm">
                    <Table.Tbody>
                      {data.flags.map((f, i) => (
                        <Table.Tr key={i}>
                          <Table.Td w={120} fw={600} tt="capitalize">
                            {f.nutrient}
                          </Table.Td>
                          <Table.Td w={110}>
                            <Badge size="sm" color={STATUS_COLORS[f.status]} variant="light">
                              {f.status.replace("_", " ")}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text fz="xs" ff="monospace" c="dimmed">
                              {f.amount}
                            </Text>
                            <Text fz="sm">{f.advice}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Card>
              )}
              {data.nextMealIdeas.length > 0 && (
                <div>
                  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: "0.05em" }}>
                    What to eat next
                  </Text>
                  <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="sm">
                    {data.nextMealIdeas.map((idea, i) => (
                      <Card key={i}>
                        <Stack gap={6}>
                          <Title order={4} fz={16}>
                            {idea.title}
                          </Title>
                          {idea.why && (
                            <Text fz="sm" c="dimmed">
                              {idea.why}
                            </Text>
                          )}
                          {idea.keyIngredients.length > 0 && <Text fz="xs">{idea.keyIngredients.join(", ")}</Text>}
                          <NutritionChips n={idea.estimatedNutrition} size="xs" />
                        </Stack>
                      </Card>
                    ))}
                  </SimpleGrid>
                </div>
              )}
              <Card>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <BulletSection title="Went well" items={data.highlights} color="lime.8" />
                  <BulletSection title="For tomorrow" items={data.tips} />
                </SimpleGrid>
              </Card>
            </Stack>
          )}
          {!data && !m.isPending && !m.error && <EmptyState title="How's today going?">Paste the log your app keeps (any JSON shape) and set targets to see what's left.</EmptyState>}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
